import { z } from 'zod';

export const ForecastedKPIItemSchema = z.object({
  label: z.string(),
  currentValue: z.number(),
  proposedValue: z.number(),
  histValue: z.number().nullish(),
  proposedValueTomorrow: z.number(),
  predictedValueTomorrow: z.number(),
  absoluteChange: z.number(),
  predictedValue: z.number(),
  percentageChange: z.number().nullable(),
  isPrimary: z.boolean(),
  isCustom: z.boolean().optional(),
  metricType: z.string(),
  isIncreasePositive: z.boolean().nullable(),
  currency: z.string().optional(),
});

const ForecastedKPIGraphItemSchema = z.object({
  dates: z.array(z.string()),
  label: z.string(),
  isPrimary: z.boolean(),
  currentValues: z.array(z.number()),
  proposedValues: z.array(z.number()),
});

const ForecastedKPIGraphSchema = z
  .object({
    daily: z.array(ForecastedKPIGraphItemSchema).nullable(),
    weekly: z.array(ForecastedKPIGraphItemSchema).nullable(),
  })
  .nullable();

const ForecastedKPISchema = z.object({
  graph: ForecastedKPIGraphSchema.optional(),
  widget: z.array(ForecastedKPIItemSchema),
  mainGoal: z.object({
    primaryMetric: z.string(),
    reverseMetric: z.string(),
  }),
});

const PrioritisationGoalSchema = z.object({
  expected: z.string(),
  actual: z.string(),
  message: z.string().optional(),
});

const MulticurrencyBudgetTypeSchema = z.object({
  currency: z.string().nullable(),
  currentBudget: z.number().nullish(),
  currentBudgetUnmargined: z.number().nullish(),
  currentCostMargined: z.number().nullish(),
  proposedBudget: z.number().nullish(),
  proposedBudgetUnmargined: z.number().nullish(),
});

const TaxonomyBudgetTypeSchema = z.object({
  currentBudget: z.number().nullable(),
  currentCostMargined: z.number().nullable(),
  currentBudgetUnmargined: z.number().nullable(),
  currentGoalValues: z.number().nullable(),
  currentPlatformBudget: z.number().nullable(),
  mediaPlatformValue: z.number().nullable(),
  proposedBudget: z.number().nullable(),
  proposedBudgetUnmargined: z.number().nullable(),
  proposedGoalValues: z.number().nullable(),
  proposedPlatformBudget: z.number().nullable(),
  undefinedCostBasedMetric: z.boolean().nullable(),
  proposedCostBasedMetric: z.number().nullable(),
  currentCostBasedMetric: z.number().nullable(),
  budgetType: z.enum(['daily', 'lifetime']).nullable(),
});

const TaxonomyChildrenBaseSchema = z.object({
  id: z.string().or(z.number()),
  name: z.string(),
  type: z.string(),
  budgetData: TaxonomyBudgetTypeSchema,
  multicurrencyBudgetData: MulticurrencyBudgetTypeSchema.optional(),
});

type TaxonomyChildrenBaseSchemaType = z.infer<
  typeof TaxonomyChildrenBaseSchema
> & {
  children: TaxonomyChildrenBaseSchemaType[];
};

export const TaxonomyChildrenSchema: z.ZodType<TaxonomyChildrenBaseSchemaType> =
  TaxonomyChildrenBaseSchema.extend({
    children: z
      .lazy(() => TaxonomyChildrenSchema.array())
      .prefault([]) as z.ZodType<TaxonomyChildrenBaseSchemaType[]>,
  });

export const TaxonomySchema = z.object({
  id: z.string().or(z.number()),
  name: z.string(),
  type: z.string(),
  budgetData: TaxonomyBudgetTypeSchema,
  children: z.array(TaxonomyChildrenSchema),
  mediaPlanId: z.string().optional(),
  target: z.string().optional(),
  forecastedKpi: ForecastedKPISchema,
  peripheralName: z.string().optional(),
  typeOfRecommendation: z.string().optional(),
  prioritisationGoal: PrioritisationGoalSchema.nullable(),
});

export const MediarowPermissionResponseSchema = z.object({
  id: z.number(),
  type: z.enum(['mediarow']),
  name: z.string(),
  permission: z.enum(['read_and_write_level', 'read_level', 'no_access_level']),
});

export const PlatformPermissionResponseSchema = z.array(
  z.object({
    id: z.number(),
    type: z.enum(['platform']),
    name: z.string(),
    children: z.array(MediarowPermissionResponseSchema),
  }),
);

export const AvailableFiltersSchema = z.object({
  platforms: z.array(z.string()),
  channels: z.array(z.string()),
  campaigns: z.array(z.string()),
});

const PlatformStatusSchema = z.enum(['error', 'success', 'pending']);

export const StatusSchema = z.enum([
  'ERROR',
  'EXPIRED',
  'ACTIVE',
  'REJECTED',
  'ACCEPTED',
  'ACCEPTING',
  'REWORKING',
]);

const PlatformStatusesDataSchema = z.object({
  objectId: z.union([z.string(), z.number()]),
  objectType: z.string(),
  status: PlatformStatusSchema,
  details: z.string().nullable(),
  forcedStatus: z.boolean(),
});

const PlatformStatusesSchema = z.object({
  id: z.number(),
  recommendationId: z.number(),
  platform: z.string(),
  data: z.array(PlatformStatusesDataSchema),
});

export const SuggestionResponseSchema = z.object({
  id: z.number(),
  originalId: z.number().nullable(),
  uuid: z.string(),
  creationDate: z.string(),
  appliedAt: z.string().nullable(),
  accountId: z.number(),
  journeyId: z.number(),
  reworkDate: z.string().nullable(),
  journeyName: z.string(),
  version: z.string(),
  taxonomy: TaxonomySchema,
  userId: z.number().nullable(),
  mediaPlanId: z.number(),
  expiredAt: z.string().nullable(),
  currency: z.string(),
  type: z.string(),
  status: StatusSchema,
  reason: z.string().nullable(),
  enabled: z.boolean(),
  description: z.string().nullish(),
  goal: z.string().optional(),
  mailWasSent: z.boolean(),
  notRead: z.boolean(),
  decisionTime: z.string().nullish(),
  decidedBy: z
    .object({
      email: z.string(),
      companyId: z.number(),
      firstName: z.string(),
      lastName: z.string(),
    })
    .nullish(),
  platformStatuses: z.array(PlatformStatusesSchema),
});

export const GenerateRequestSchema = z.object({
  suggestionId: z.number(),
  mediaPlanId: z.number(),
  mediaRowIds: z.array(z.number()),
});

export const ListResponseSchema = z.object({
  items: z.array(SuggestionResponseSchema),
  page: z.number(),
  pages: z.number(),
});

export const TabsSchema = z.object({
  accepted: z.boolean(),
  error: z.boolean(),
  expired: z.boolean(),
  new: z.boolean(),
  rejected: z.boolean(),
});

export const GetFilterResponseSchema = AvailableFiltersSchema.extend({
  tabs: TabsSchema,
});

export const GetFilterRequestSchema = AvailableFiltersSchema.extend({
  dateFrom: z.string(),
  dateTo: z.string(),
  sortBy: z.string(),
  statuses: z.string(),
  campaignName: z.string(),
  campaignId: z.number().optional(),
  version: z.string().optional(),
});

export const ListRequestSchema = GetFilterRequestSchema.extend({
  page: z.number().optional(),
  pageSize: z.number(),
});

export const BulkPublishRequestSchema = z.object({
  recommendationIds: z.array(z.number()),
  campaignIds: z.array(z.number()),
  version: z.string().optional(),
});

export const SuggestionPlatformSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const SuggestionPlatformsResponseSchema = z.object({
  integratedPlatforms: z.array(SuggestionPlatformSchema), // automatic platforms
  ghostPlatforms: z.array(SuggestionPlatformSchema), // manual platforms
});
